import React from 'react'
import type { TFunction } from 'i18next'
import type { ElectricityAnalysis } from '@popety-io/types'
import {
  formatPrice,
  calculatePercentageDifference,
} from '../electricityService'
import {
  ComparisonCard,
  ComparisonCardTitle,
  ComparisonBarsContainer,
  ComparisonBarRow,
  ComparisonBarLabel,
  ComparisonBarTrack,
  ComparisonBar,
  ComparisonBarValue,
  ComparisonDiffText,
} from '../ElectricitySection.styles'

export interface GeographicComparisonCardProps {
  data: ElectricityAnalysis
  t: TFunction
}

const getDiffColor = (diff: number): 'success' | 'danger' =>
  diff < 0 ? 'success' : 'danger'

export const GeographicComparisonCard: React.FC<
  GeographicComparisonCardProps
> = ({ data, t }) => {
  const { current_data, price_comparison } = data

  if (!price_comparison) return null

  const locationPrice = current_data.total_price_min
  const cantonPrice = price_comparison.canton_median_price
  const nationalPrice = price_comparison.national_median_price

  const maxValue = Math.max(locationPrice, cantonPrice, nationalPrice) * 1.1

  const cantonDiff = calculatePercentageDifference(locationPrice, cantonPrice)
  const nationalDiff = calculatePercentageDifference(
    locationPrice,
    nationalPrice
  )

  const locationName = t('comparison.thisLocation')

  return (
    <ComparisonCard>
      <ComparisonCardTitle>{t('comparison.title')}</ComparisonCardTitle>

      <ComparisonBarsContainer>
        {/* Location bar */}
        <ComparisonBarRow>
          <ComparisonBarLabel>{locationName}</ComparisonBarLabel>
          <ComparisonBarTrack>
            <ComparisonBar
              $width={(locationPrice / maxValue) * 100}
              $color="neutral"
            />
          </ComparisonBarTrack>
          <ComparisonBarValue $color="neutral">
            {formatPrice(locationPrice, t('gridOperators.unit'))}
          </ComparisonBarValue>
        </ComparisonBarRow>

        {/* Canton bar */}
        <ComparisonBarRow>
          <ComparisonBarLabel>{t('comparison.canton')}</ComparisonBarLabel>
          <ComparisonBarTrack>
            <ComparisonBar
              $width={(cantonPrice / maxValue) * 100}
              $color={getDiffColor(cantonDiff)}
            />
          </ComparisonBarTrack>
          <ComparisonBarValue $color={getDiffColor(cantonDiff)}>
            {formatPrice(cantonPrice, t('gridOperators.unit'))}
          </ComparisonBarValue>
        </ComparisonBarRow>

        {/* Canton diff text */}
        <ComparisonDiffText $positive={cantonDiff < 0}>
          {Math.abs(cantonDiff).toFixed(1)}%{' '}
          {cantonDiff < 0
            ? t('comparison.belowCantonMedian')
            : t('comparison.aboveCantonMedian')}
        </ComparisonDiffText>

        {/* National bar */}
        <ComparisonBarRow>
          <ComparisonBarLabel>{t('comparison.switzerland')}</ComparisonBarLabel>
          <ComparisonBarTrack>
            <ComparisonBar
              $width={(nationalPrice / maxValue) * 100}
              $color={getDiffColor(nationalDiff)}
            />
          </ComparisonBarTrack>
          <ComparisonBarValue $color={getDiffColor(nationalDiff)}>
            {formatPrice(nationalPrice, t('gridOperators.unit'))}
          </ComparisonBarValue>
        </ComparisonBarRow>

        {/* National diff text */}
        <ComparisonDiffText $positive={nationalDiff < 0}>
          {Math.abs(nationalDiff).toFixed(1)}%{' '}
          {nationalDiff < 0
            ? t('comparison.belowNationalMedian')
            : t('comparison.aboveNationalMedian')}
        </ComparisonDiffText>
      </ComparisonBarsContainer>
    </ComparisonCard>
  )
}
